import { Button, Typography } from '@mui/material';
import { addCredential, updateCredential, verifyCredential } from 'js/actions';
import { addCatalogConfig, updateCatalogConfig } from 'js/actions/catalogConfigs';
import {
  ADD_CATALOG_CONFIG_REQUEST,
  ADD_CREDENTIAL_REQUEST,
  ADD_GEN_AI_CONFIG_REQUEST,
  PROBE_CREDENTIAL_REQUEST,
  UPDATE_CATALOG_CONFIG_REQUEST,
  UPDATE_CREDENTIAL_REQUEST,
  UPDATE_GEN_AI_CONFIG_REQUEST,
} from 'js/constants/actionTypes';
import {
  EXTERNAL_INTEGRATIONS,
  Integration,
  integrationTypes,
} from 'js/constants/externalIntegrations';
import UIUtils from 'js/lib/UIUtils';
import { getEmptyRequiredFields } from 'js/lib/ValidationUtils';
import { filter, find, isEmpty, map, has } from 'lodash';
import { useCurrentUser } from 'new/hooks/adapters/useCurrentUser';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styles from './DatasetCatalog/DatasetCatalog.module.scss';
import { ThunkDispatch } from 'redux-thunk';
import { Action } from 'redux';
import { addGenAiConfig, updateGenAiConfig } from 'js/actions/genAiConfigs';
import { FieldBuilderRedesigned as FieldBuilder } from 'new/elements/FieldBuilderRedesigned/FieldBuilder';

const actions = {
  [integrationTypes.data_catalog]: {
    update: updateCatalogConfig,
    add: addCatalogConfig,
  },
  [integrationTypes.gen_ai]: {
    update: updateGenAiConfig,
    add: addGenAiConfig,
  },
};

interface IntegrationConfigurationProps {
  integration: Integration;
  integrationType: string;
}

const FIXED_AUTH_FIELDS = [
  {
    name: 'cred_name',
    display_name: 'Credential Name',
    description: 'Please set a name for this credential.',
    type: 'string',
    required: true,
  },
  {
    name: 'cred_desc',
    display_name: 'Credential Description',
    description: 'Please set a meaningful description for this Nexla credential.',
    type: 'string',
  },
];

const Fields = ({ fields, values, onChange }) => {
  const [, set] = useState();
  return (
    <>
      {map(
        filter(
          fields,
          (field) =>
            !UIUtils.shouldHideField(field, values) && UIUtils.shouldRenderField(field, values),
        ),
        (field) => (
          <FieldBuilder
            key={field.name}
            id={field.name}
            field={field}
            currentFieldValue={
              field.type === 'string'
                ? (values[field.name] ?? (has(field, 'default') ? field.default : ''))
                : values[field.name]
            }
            isRequired={field.required}
            showRequiredIndicator={false}
            onChange={onChange}
          />
        ),
      )}
    </>
  );
};

const Authentication = ({ name, credentialId, onUpdateCredentialId }) => {
  const dispatch = useDispatch<ThunkDispatch<any, any, Action>>();
  const {
    entities: { credentials },
  } = useSelector((state: any) => state);
  const credential = credentials[credentialId];
  const [error, setError] = useState(null);
  const [isEditMode, setIsEditMode] = useState(!credentialId);
  const [fieldValues, setFieldValues] = useState({
    cred_name: credential?.name || '',
    cred_desc: credential?.description || '',
    ...(credential?.credentials_non_secure_data || {}),
  });
  const isLoading = useIsLoading(
    ADD_CREDENTIAL_REQUEST,
    UPDATE_CREDENTIAL_REQUEST,
    PROBE_CREDENTIAL_REQUEST,
  );
  const { isAdmin } = useCurrentUser();
  const fields = [...FIXED_AUTH_FIELDS, ...EXTERNAL_INTEGRATIONS[name].credential.fields];

  const renderEditMode = () => {
    return (
      <>
        <Fields fields={fields} values={fieldValues} onChange={onFieldUpdated} />
        {error && (
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        )}
        <div className={styles.buttonsContainer}>
          {credential && (
            <Button
              variant="contained"
              color="info"
              disabled={isLoading}
              onClick={onCancelAuthClicked}
            >
              Cancel
            </Button>
          )}
          <Button variant="contained" color="primary" disabled={isLoading} onClick={onAuthClicked}>
            Authenticate
          </Button>
        </div>
      </>
    );
  };

  const renderViewMode = () => {
    return (
      <>
        <Typography variant="body2">
          Credential: <b>{credential.name}</b> (ID: {credential.id})
        </Typography>
        {isAdmin && (
          <div className={styles.buttonsContainer}>
            <Button variant="contained" color="primary" onClick={onEditClicked}>
              Edit Credential
            </Button>
          </div>
        )}
      </>
    );
  };

  const onAuthClicked = () => {
    setError(null);

    const data = UIUtils.configWithMissingDefaults(fieldValues, fields);
    const missingFields = getEmptyRequiredFields(fields, data);

    if (!isEmpty(missingFields)) {
      setError(
        'Please enter the following required fields: ' +
          map(missingFields, 'display_name').join(', '),
      );
      return;
    }

    const { cred_name, cred_desc } = data;
    const { type, config } = EXTERNAL_INTEGRATIONS[name].credential;
    const credInfo = {
      cred_name,
      cred_desc,
      ...UIUtils.replacePlaceholdersGeneric(config, data),
    };

    dispatch(
      credentialId ? updateCredential(credentialId, credInfo) : addCredential(type, credInfo),
    )
      .then((response) => {
        const { result } = response;

        onUpdateCredentialId(result);

        return dispatch(verifyCredential(result));
      })
      .then(() => {
        setIsEditMode(false);
      })
      .catch(() => {
        setError('Unable to authenticate with these credentials.');
      });
  };

  const onCancelAuthClicked = () => {
    setIsEditMode(false);
  };

  const onEditClicked = () => {
    setError(null);
    setFieldValues({
      cred_name: credential?.name || '',
      cred_desc: credential?.description || '',
      ...(credential?.credentials_non_secure_data || {}),
    });
    setIsEditMode(true);
  };

  const onFieldUpdated = (name, value) => {
    setFieldValues({ ...fieldValues, [name]: value });
  };

  return <div className={styles.section}>{isEditMode ? renderEditMode() : renderViewMode()}</div>;
};

const Configuration = ({ integrationType, integration, credentialId, integrationConfig }) => {
  const dispatch = useDispatch();
  const [fieldValues, setFieldValues] = useState(integrationConfig?.config || {});

  const [error, setError] = useState(null);
  const isLoading = useIsLoading(
    ADD_GEN_AI_CONFIG_REQUEST,
    UPDATE_GEN_AI_CONFIG_REQUEST,
    ADD_CATALOG_CONFIG_REQUEST,
    UPDATE_CATALOG_CONFIG_REQUEST,
  );
  const { isAdmin } = useCurrentUser();
  let fields = integration.fields;

  if (!isAdmin) {
    fields = map(fields, (field) => ({ ...field, disabled: true }));
  }

  const onFieldUpdated = (name, value) => {
    setFieldValues({ ...fieldValues, [name]: value });
  };

  const onSaveClicked = () => {
    if (!credentialId) {
      setError('Please configure a credential.');
      return;
    }

    const config = UIUtils.configWithMissingDefaults(fieldValues, fields, false);
    const missingFields = getEmptyRequiredFields(fields, config);

    if (!isEmpty(missingFields)) {
      setError(
        'Please enter the following required fields: ' +
          map(missingFields, 'display_name').join(', '),
      );
      return;
    }

    const byTypeActions = actions[integrationType];

    dispatch(
      integrationConfig
        ? byTypeActions.update(integrationConfig.id, { config })
        : byTypeActions.add(
            integrationType === integrationTypes.data_catalog
              ? {
                  name: integration.meta.name,
                  description: `Config for ${integration.meta.name}`,
                  data_credentials_id: credentialId,
                  status: 'PAUSED',
                  mode: 'manual', // temporary hardcoding to work around API defaults
                  config,
                }
              : {
                  name: integration.meta.name,
                  type: integration.credential.type,
                  description: `Config for ${integration.meta.name}`,
                  data_credentials_id: credentialId,
                  status: 'PAUSED',
                  config,
                },
          ),
    );
  };

  return (
    <div className={styles.section}>
      <Fields fields={fields} values={fieldValues} onChange={onFieldUpdated} />
      {error && (
        <Typography variant="body2" color="error">
          {error}
        </Typography>
      )}
      {isAdmin && (
        <div className={styles.buttonsContainer}>
          <Button variant="contained" color="primary" disabled={isLoading} onClick={onSaveClicked}>
            Save
          </Button>
        </div>
      )}
    </div>
  );
};

export const IntegrationConfiguration = ({
  integration,
  integrationType,
}: IntegrationConfigurationProps) => {
  const {
    entities: { catalogConfigs, genAiConfigs },
  } = useSelector((state: any) => state);
  const integrationConfig =
    integrationType === integrationTypes.data_catalog
      ? find(catalogConfigs, { name: integration.meta.name })
      : find(genAiConfigs, { type: integration.meta.name });
  const [credentialId, setCredentialId] = useState(integrationConfig?.data_credentials);
  const { isAdmin } = useCurrentUser();
  const displayName = integration.meta.displayName || integration.meta.name;

  if (!integrationConfig && !isAdmin) {
    return (
      <div className={styles.dataCatalogConfiguration}>
        <Typography variant="body3">
          Nexla has not been connected to <b>{displayName}</b>. Please ask the organization
          administrator to setup the connection.
        </Typography>
      </div>
    );
  }

  return (
    <div className={styles.dataCatalogConfiguration}>
      <Typography className={styles.sectionHeading} variant="subheading">
        Authentication
      </Typography>
      <Authentication
        name={integration.meta.name}
        credentialId={credentialId}
        onUpdateCredentialId={setCredentialId}
      />
      <Typography className={styles.sectionHeading} variant="subheading">
        Configuration
      </Typography>
      <Configuration
        integration={integration}
        integrationType={integrationType}
        credentialId={credentialId}
        integrationConfig={integrationConfig}
      />
    </div>
  );
};
